import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  User,
  initializeAuth,
  validateSession,
  getStoredToken,
  logout as apiLogout,
} from '../api/auth';

interface AuthContextType {
  currentUser: User | null;
  isAuthenticated: boolean;
  authLoading: boolean;
  isAdmin: boolean;
  login: (user: User) => void;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  const isAdmin = currentUser?.role === 'administrator';

  // 인증 초기화: 토큰이 있으면 백엔드로 유효성 검증
  useEffect(() => {
    const token = getStoredToken();
    if (token) {
      setAuthLoading(true);
      validateSession().then(authState => {
        setIsAuthenticated(authState.isAuthenticated);
        setCurrentUser(authState.user);
        setAuthLoading(false);
      });
    } else {
      const authState = initializeAuth();
      setIsAuthenticated(authState.isAuthenticated);
      setCurrentUser(authState.user);
      setAuthLoading(false);
    }

    // 세션 만료 이벤트 처리 (client.ts의 forceLogout에서 발생)
    const handleSessionExpired = () => {
      setIsAuthenticated(false);
      setCurrentUser(null);
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, []);

  const login = useCallback((user: User) => {
    setCurrentUser(user);
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setCurrentUser(null);
    setIsAuthenticated(false);
  }, []);

  const updateUser = useCallback((user: User) => {
    setCurrentUser(user);
  }, []);

  const value: AuthContextType = {
    currentUser,
    isAuthenticated,
    authLoading,
    isAdmin,
    login,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
